/**
 * 报表分析页面
 * 可以从自助分析系统的 我的分析页面点击查看跳转到此页面
 */
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {connect, useParams, useIntl} from "umi";
import {useRequest} from "ahooks";
import moment from "moment";
import {Button, Justify, Layout, message, StatusTip} from "tea-component";
import ReportFilters from "@/pages/dashboard/pages/datalab/ReportAnalysis/ReportFilters";
import ReportChart from "@/pages/dashboard/pages/datalab/ReportAnalysis/ReportChart";
import ReportTable from "@/pages/dashboard/pages/datalab/ReportAnalysis/ReportTable";
import {downloadFileWithFileFormat, useNotificationOfDownloadTable} from "@/pages/dashboard/components/DownloadFormatModal";
import {
	getDatalabAnalysisDashboardDownload,
	getDatalabDashboardDetail,
	getFilterOptions
} from "@/pages/dashboard/services/datalab";
import useAllData from "@/pages/dashboard/pages/datalab/ReportAnalysis/untils/useAllData";
import {BaseDate} from "@/pages/dashboard/pages/datalab/ReportAnalysis/untils/baseDate";
import {useBuriedPoint} from "@/pages/home/utils/customHook";

import './index.scss';


const {Content} = Layout;
const {LoadingTip, ErrorTip} = StatusTip;

function useFilters(dashboard_uuid, gameCode) {
	const [filters, setFilters] = useState([]);
	const [filterValues, setFilterValues] = useState({});
	const [dateGranularity, setDateGranularity] = useState({});
	const [dimensionTypes, setDimensionTypes] = useState([]);
	
	const {data = {}, loading, error} = useRequest(
		() => getDatalabDashboardDetail({dashboard_uuid, game_code: gameCode}),
		{
			refreshDeps: [dashboard_uuid, gameCode],
			onSuccess(result: { default_filter: any; table_infos: any; filter: any; }, params) {
				
				const defaultFilterValues = JSON.parse(result?.default_filter || "{}")?.filterValues;
				const tableInfos = JSON.parse(result?.table_infos || "{}");
				const defaultFilters = JSON.parse(result?.filter || "{}");
				const dimensionTypes: string[] = [];
				const columns = defaultFilters?.group_by?.map?.(group => {
					const filter = defaultFilters?.filters?.filters?.find((item) => item.column === group.column && item.table_id === group.table_id);
					const query = filter?.value || []; // 可以兼容多维分析页面传递的格式前后变动.
					if (group.column_type !== 'date') dimensionTypes.push(group.label);
					return {
						query,
						queryType: 0,
						name: group.column,
						tableId: group.table_id,
						type: group.column_type,
						label: group.label,
						label_en: group.label_en,
						operation: group.column_type === 'date' ? 'between' : filter?.operation || 'include',
						options: [],
						isFirst: true
					};
				}) || [];
				let filterValuesObj: any = {};
				const dateGranularity = defaultFilterValues.reduce((previous, item) => {
					if (BaseDate.values.includes(item.column_type)) {
						const table = tableInfos.find((table: any) => table.table_id === item.table_id);
						previous[item.column + item.table_id] = table?.granularity === 'day' ? "date" : table?.granularity;
					}
					return previous;
				}, {});
				setDateGranularity(dateGranularity);
				defaultFilterValues?.map?.(item => {
					const key = item.column + item.table_id;
					filterValuesObj[key] = item.value;
				});
				setFilterValues(filterValuesObj);
				setFilters(columns);
				setDimensionTypes(dimensionTypes)
			}
		}
	);
	
	return {
		owner: data?.user_name,
		reportName: data?.name,
		reportNameCn: data?.name_cn,
		filtersLoading: loading,
		filtersError: error,
		taskId: data?.task_id,
		filters,
		filterValues,
		dateGranularity,
		dimensionTypes,
		setFilterValues,
		setFilters
	};
}

function useFilterOptions(filters=[], filterValues={}, taskId, dashboardUuid, gameCode) {
	/**
	 * 由于图例数据和表格中的数据 在中英转换时需要用到 筛选器下拉选项的数据
	 * 所以页面进入的时候, 将所有筛选器的下拉选项全部请求回来
	 * localNameMap 用于保存中英映射关系的数据
	 * getDynamicLocalText 提供给图表使用, 用于切换中英文本
	 */
	const [loading, setLoading] = useState(true);
	// 记录筛选器下拉选映射关系
	const [localNameMap, setLocalNameMap] = useState({"对比项": {
			"对比项一": {
				display: "对比项一",
				display_en: "Compare1",
			},
			"对比项二": {
				display: "对比项二",
				display_en: "Compare2",
			},
		}});
	const getDynamicLocalText = useCallback((type, name, isEn) => {
		const item = localNameMap?.[type]?.[name];
		return item ? (isEn ? (item.display_en || item.display) : item.display) : name;
		
	}, [localNameMap])
	
	//
	useEffect(() => {
		const allPromises = [];
		filters?.map((item, index) => {
			// 时间类型不需要发起options请求
			if (item?.operation === 'between') {
				allPromises.push(Promise.reject())
				return;
			}
			
			const params = {
				from: "task",
				from_id: String(taskId),
				query_type: item.queryType,
				query: item.query,
				page_no: 1,
				page_size: 5000,
				task_id: String(taskId),
				table_id: item.tableId,
				dim_name: item.name,
				dashboard_uuid: String(dashboardUuid),
				game_code: gameCode
			};
			const optionPromise = getFilterOptions(params);
			allPromises.push(optionPromise)
		})
		allPromises.length > 0 && Promise.allSettled(allPromises).then((result) => {
			try {
				result.forEach(({status, value}, index) => {
					if (status === "fulfilled") {
						const newLocalNameMap = {} // 中英映射关系
						const options = []; // 筛选器下拉选项
						const newValues = []; // 筛选器为All时, 筛选器组件的value值
						value?.value_list?.forEach?.(item => {
							options.push({ text: item?.display, value: item?.value, text_en: item?.display_en || item?.display })
							newLocalNameMap[item.display] = item;
							newValues.push(item.value)
						});
						const currentFilter = filters[index];
						currentFilter.options = options;
						localNameMap[currentFilter?.label] = newLocalNameMap;
						if (filterValues[currentFilter.name + currentFilter.tableId]?.length === 0) {
							filterValues[currentFilter.name + currentFilter.tableId] = newValues;
						}
					}
				})
				setLocalNameMap({...localNameMap})
			} catch (e) {
				console.log("筛选器下拉选请求错误", e);
			}
			setLoading(false);
		}).catch(e => {
			console.log("筛选器下拉选请求错误", e);
			setLoading(false)
		})
		
	}, [filters]);
	
	
	
	return {
		loading,
		getDynamicLocalText
	}
}

function ReportAnalysis({gameCode}) {
	
	const {dashboardid} = useParams();
	if (!dashboardid) {
		// 提示报表id缺失
		message.error({
			content: "报表id缺失"
		});
	}
	const { locale } = useIntl();
	const isEn = locale == "en-US";
	// 埋点
	const logObj = {
		pageId: '202601',
		pageName: 'datalabReportAnalysis',
		extInfo: 'datalabReportAnalysis',
		pagePath: `/datalab/report/${dashboardid}`,
		uid: gameCode,
		uidType: 'game_code'
	};
	useBuriedPoint(logObj);
	const dashboardUuid = Number(dashboardid);
	const {
		owner,
		reportName,
		reportNameCn,
		filtersLoading,
		filtersError,
		taskId,
		filters = [],
		filterValues = {},
		dateGranularity,
		dimensionTypes,
		setFilterValues,
		setFilters
	} = useFilters(dashboardUuid, gameCode);
	const {
		loading: optionsLoading,
		getDynamicLocalText
	} = useFilterOptions(filters, filterValues, taskId, dashboardUuid, gameCode)

	
	// 设置图表上传参数queries
	const queries = useMemo(() => {
		const filtersParam = filters?.map?.(filter => ({
			column: filter.name,
			operation: filter.operation,
			table_id: filter.tableId,
			value: filter?.options?.length === filterValues[filter.name + filter.tableId]?.length ? [] : filterValues[filter.name + filter.tableId] || []
		})) || [];
		return {
			filters: filtersParam
		};
	}, [filterValues, filters]);
	// filter主要记录了筛选器的类型和 options选项, filterValues是用户选择的值
	// 但由于在 ReportFilters中 会请求筛选器的options, 导致filter对象改变, 所以queries的依赖不能有filter, 否则会发出多次请求.
	// filter 和 filterValues都是在请求DashboardDetail接口成功后同时修改的, 所以只依赖filterValues, 就可以得到准确的图表上传参数queries
	// 在ReportFilters中, 不会改变筛选器的个数或类型, 只会改变filter的options, 而options和图表上传参数queries无关, 所以即便不依赖filters也没有关系.
	
	// 请求图表和表格数据
	const {loading, error, data, setData, setLoading} = useAllData(dashboardUuid, queries);
	
	useEffect(() => {
		setLoading(true);
		setData({}); // 在承接页面之间切换时, 避免图表闪现上一个页面的数据
	}, [dashboardUuid, gameCode]);
	const { setNoticeStatus } = useNotificationOfDownloadTable(`${reportName}-${owner}`)
	
	
	
	
	/**
	 * 下载文件
	 */
	let sortColumn = null; // 排序字段
	const download = async () => {
		try {		// 展示正在下载中通知
			setNoticeStatus("loading");
			const orderby = [];
			if (sortColumn) orderby.push(sortColumn);
			const params = {dashboard_uuid: dashboardUuid, queries: {...queries, orderby}, language: isEn ? "en" : "zh"};
			const res = await getDatalabAnalysisDashboardDownload(params);
			if (res) {
				const fileName = `${`${reportName}-${owner}`.replace(/\u0020/g, '_')} ${moment().format('YYYY_MM_DD HH_mm_ss')}`
				
				await downloadFileWithFileFormat(res, fileName, {
					onDownloadSuccess() {
						setNoticeStatus("success");
					},
					onDownloadError() {
						setNoticeStatus("error");
					}
				})
			} else {
				setNoticeStatus("error");
			}
			
		} catch (e) {
			setNoticeStatus("error");
		}

	};
	return (<Content className="oit-layout--fixheader report-chart">
		
		<Content.Header title={!(filtersLoading || filtersError) ? `${isEn ? reportName : reportNameCn}-${owner}` : ''}/>
		{
			(filtersLoading || optionsLoading)  && (<div className="status-loading"><LoadingTip/></div>)
		}
		{
			!!filtersError && (<div className="status-loading"><ErrorTip/></div>)
		}
		
		{
			!(filtersLoading || filtersError || optionsLoading) && (
				<Content.Body full>
					{/* 筛选器, 除了时间筛选器, 都使用 SelectMultiple */}
					<Justify
						style={{marginBottom: "10px"}}
						left={(<>
							<ReportFilters
								isEn={isEn}
								gameCode={gameCode}
								filters={filters}
								dateGranularity={dateGranularity}
								setFilters={setFilters}
								filterValues={filterValues}
								setFilterValues={setFilterValues}
								taskId={taskId}
								dashboardUuid={dashboardUuid}
								onChange={(value) => {
									setFilterValues(value);
								}}
							/>
						</>)}
						right={
							<Button
								className="download-btn overall-conext-icon"
								onClick={download}
								type={"icon"}
								icon={"download"}
							/>
						}
					/>
					{/* 图表 开始*/}
					<ReportChart
						isEn={isEn}
						queries={queries}
						loading={loading}
						error={error}
						dimensionTypes={dimensionTypes}
						chartResponse={data?.chart_data}
						pieResponse={data?.chart_sum_data}
						metrics={data?.metrics || []}
						metricsEn={data?.metrics_en || []}
						getDynamicLocalText={getDynamicLocalText}
					/>
					{/* 图表 结束*/}
					
					{/* 表格 开始*/}
					<ReportTable
						isEn={isEn}
						dashboardUuid={dashboardUuid}
						queries={queries}
						sortChange={(sort) => {
							sortColumn = sort;
						}}
						loading={loading}
						data={data?.table_data}
						count={data?.count || 0}
						columnName={data?.column_name}
						getDynamicLocalText={getDynamicLocalText}
					/>
					{/* 表格 结束*/}
				</Content.Body>
			)
		}
	
	</Content>);
}

export default connect((state: any) => {
	return {
		gameCode: state.dashboard?.currentGame?.game_code,
	};
})(ReportAnalysis);
